import React, { useEffect } from 'react';
import { BrowserRouter as Router, useRoutes, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd-mobile';
import zhCN from 'antd-mobile/es/locales/zh-CN';

// 导入路由配置
import routes from './routes';

// Styles
import './App.css';
import { AIResponseCard } from './components';
import WebSocketNotification from './components/WebSocketNotification';
import ToAdminFixedBtn from './components/ToAdminEntry/ToAdminFixedBtn';
import { NotificationProvider } from './hooks/useNotification';
import onlineStatusService from './lib/services/onlineStatusService';
import { useUserStore } from './stores/userStore';
import { isLoggedIn } from './lib/auth';
import { isDingTalkEnv, hideDingTalkRightButton } from './lib/dingTalkUtils';

// 初始化全局服务
const initGlobalServices = () => {
  // 初始化在线状态服务
  onlineStatusService.init(30); // 保存最近30个登录用户
};

// 用户数据初始化组件
const UserDataInitializer: React.FC = () => {
  const initialize = useUserStore(state => state.initialize);
  const initialized = useUserStore(state => state.initialized);
  const loading = useUserStore(state => state.loading);
  const permissions = useUserStore(state => state.permissions);
  
  useEffect(() => {
    console.log('isLoggedIn()==========>', isLoggedIn());
    console.log('initialized==========>', initialized);
    console.log('loading==========>', loading);
    console.log('permissions length==========>', permissions.length);
    
    // 只有在用户已登录且未初始化时才进行初始化
    if (isLoggedIn() && !initialized && !loading) {
      console.log('UserDataInitializer: 开始初始化用户数据');
      initialize().catch(error => {
        console.error('UserDataInitializer: 初始化用户数据失败', error);
      });
    }
  }, [initialize, initialized, loading]);
  
  return null;
};

// 路由组件
const AppRoutes = () => {
  const routeElements = useRoutes(routes);
  return routeElements;
};

// 全局WebSocket连接管理组件
const GlobalWebSocketManager: React.FC = () => {
  useEffect(() => {
    // 这个组件只负责维持WebSocket连接
    // 实际的WebSocket逻辑在WebSocketNotification组件中
    console.log('GlobalWebSocketManager: 确保WebSocket连接在页面切换时保持');
    
    return () => {
      // 应用卸载时的清理逻辑（如果需要）
    };
  }, []);
  
  // 这个组件不渲染任何UI
  return null;
};

// 钉钉环境配置组件
const DingTalkEnvironmentConfig: React.FC = () => {
  const location = useLocation();
  
  useEffect(() => {
    // 检测是否是钉钉环境
    if (isDingTalkEnv()) {
      console.log('🔍 检测到钉钉环境，路由变化，重新配置导航栏...', location.pathname);
      
      // 延迟一下，确保钉钉导航栏已经渲染
      const timer = setTimeout(() => {
        // 隐藏右上角的三个点按钮
        hideDingTalkRightButton()
          .then(() => {
            console.log('✅ 钉钉右上角按钮已隐藏', location.pathname);
          })
          .catch((error) => {
            console.error('❌ 隐藏钉钉右上角按钮失败', error);
          });
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      console.log('ℹ️ 非钉钉环境，跳过钉钉配置');
    }
  }, [location.pathname]); // 监听路由变化
  
  // 这个组件不渲染任何UI
  return null;
};

function App() {
  // 初始化全局服务
  useEffect(() => {
    initGlobalServices();
  }, []);
  
  return (
    <ConfigProvider locale={zhCN}>
      <NotificationProvider>
        <Router>
          <div className="App">
            <AppRoutes />

            {/* AI响应卡片 */}
            <AIResponseCard />

            {/* WebSocket通知组件 */}
            <WebSocketNotification />

            {/* 全局WebSocket连接管理 */}
            <GlobalWebSocketManager />

            {/* 钉钉环境配置 */}
            <DingTalkEnvironmentConfig />

            {/* 用户数据初始化 */}
            <UserDataInitializer />

            {/* 全局管理后台入口按钮 */}
            <ToAdminFixedBtn />
          </div>
        </Router>
      </NotificationProvider>
    </ConfigProvider>
  );
}

export default App;